import { LitElement, PropertyValues, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import _ from "lodash";
import { watch } from "vue";

import { Domain, useDomainManager } from "@/composables/domain-manager";
import { useEditManager } from "@/composables/edit-manager";
import { useOrganizationStore } from "@/composables/organization-manager";
import { useProcessManager } from "@/composables/process-manager";
import { Product, useProductManager } from "@/composables/product-manager";
import { useStekManager } from "@/composables/stek-manager";
import {
    type Worktree,
    useWorktreeManager,
    useWorktreeStore,
} from "@/composables/worktree-manager";
import router from "@/router";
import { type DomainInTree, useDomainsStore } from "@/stores/domains-store";
import { EntityType } from "@/stores/entity-store";
import {
    Process,
    type ProcessInTree,
    useProcessesStore,
} from "@/stores/processes-store";
import { type ProductInTree, useProductsStore } from "@/stores/products-store";

interface BaseMenuState {
    creating?: boolean;
    open: boolean;
    selected: boolean;
    renaming: boolean;
    newName?: string;

    // When 'placeholder' is true, the domain doesn't actually exist yet.
    // This is used as a transitionary state when creating a new domain,
    // to allow the UI to show a domain placeholder during creation.
    placeholder?: boolean;

    expandTimeoutId?: ReturnType<typeof setTimeout>;
    dropTarget?: boolean;
}

interface DomainMenuState extends BaseMenuState {
    newProductName?: string;
}

interface ProductMenuState extends BaseMenuState {
    newProcessName?: string;
}

// The same interfaces as worktree.DomainInTree, but with a little state
// information sprinkled on top. These interfaces are specifically meant
// for this component.
interface DomainInMenu extends DomainInTree {
    state: DomainMenuState;
    products: ProductInMenu[];
}

interface ProductInMenu extends ProductInTree {
    domain: DomainInMenu;
    state: ProductMenuState;
    processes: ProcessInMenu[];
}

interface ProcessInMenu extends ProcessInTree {
    product: ProductInMenu;
    state: BaseMenuState;
}

interface DragInfo {
    entity: ProductInMenu | ProcessInMenu;
    dropTarget?: DomainInMenu | ProductInMenu;
    dropSuccess: boolean;
}

// A special type which has no parents or state. It is used to communicate
// that a new domain has to be created.
interface NewDomain {
    name: string;
}

function isDomainInMenu(
    entity: DomainInMenu | ProductInMenu | ProcessInMenu | NewDomain
): entity is DomainInMenu {
    return entity && "products" in entity;
}

function isProductInMenu(
    entity: DomainInMenu | ProductInMenu | ProcessInMenu | NewDomain
): entity is ProductInMenu {
    return entity && "processes" in entity;
}

function isProcessInMenu(
    entity: DomainInMenu | ProductInMenu | ProcessInMenu | NewDomain
): entity is ProcessInMenu {
    return entity && "product" in entity;
}

function getDefaultMenuState() {
    return {
        open: false,
        selected: false,
        renaming: false,
        creating: false,
    };
}

@customElement("c-stekz-tree")
export class StekzTreeComponent extends LitElement {
    manager = useWorktreeManager();
    store = useWorktreeStore();
    isEditing = useEditManager().isEditing;

    constructor() {
        super();

        watch(
            this.manager.worktree,
            () => {
                this.loading = true;
                this.updateMenu();
            },
            { immediate: true }
        );

        watch(
            this.isEditing,
            () => {
                this.updateMenu();
            },
            { immediate: true }
        );
    }

    static styles = css`
        :host {
            --ul-indent: 24px;
            --ul-icon-size: 24px;
            --expand-icon-size: 20px;
        }
        input {
            font-size: 1em;
            padding: 6px 16px;
            z-index: 2200;
        }
        .file-list.root {
            margin-left: 0;
            padding-left: 0;
        }

        .file-list,
        .file-list ul {
            list-style-type: none;
            font-size: 1em;
            color: var(--text-color);
            line-height: 1.5em;
            margin-left: -17px;
            padding-left: var(--ul-indent);
            border-left: 1px dotted #aaa;
        }
        .file-list li {
            position: relative;
        }

        li.node-root ul {
            transition: all 0.2s ease-in-out;
            padding-left: 0;
            margin-left: var(--ul-indent);
        }

        li.node-root.new {
            margin-bottom: 3px;
        }

        li.node-root.closed > ul {
            opacity: 0;
            max-height: 0;
        }
        li.node-root.open > ul {
            opacity: 1;
            display: block;
        }

        div.new-entity {
            color: #aaa;
        }

        li a {
            text-decoration: none;
            color: var(--text-color);
        }

        /* When dragging an item, we want to hide it from the list */
        li.dragging {
            transition: 0.01s;
            transform: translateX(-9999px);
        }

        .draggable * {
            cursor: move; /* fallback if grab cursor is unsupported */
            cursor: grab;
        }

        .draggable .entity-title:hover:after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border: 2px solid var(--stekz-primary-color);
            pointer-events: none; /* Optional: allows clicks to pass through */
        }

        .drop-target > .entity-title > .entity-name:after {
            content: "";
            position: absolute;
            bottom: 0;
            right: 0;
            width: calc(100% - var(--ul-indent));
            height: 2px;
            background: var(--stekz-primary-color);
        }

        .file-list li .entity-name {
            display: block;
            position: relative;
            padding: 8px;
            padding-left: calc(var(--ul-icon-size) + var(--ul-indent));
            margin-left: var(--expand-icon-size);
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .file-list li.selected > .entity-title {
            background: var(--surface-background);
        }
        .file-list li .entity-title:hover {
            background: var(--stekz-primary-background);
        }

        .file-list li .entity-name:before {
            display: block;
            position: absolute;
            top: 10px;
            left: calc(var(--ul-indent) / 2);
            width: var(--ul-icon-size);
            height: var(--ul-icon-size);
            mask-size: cover;
            /* background-color fills the svgs */
            background-color: var(--text-color);
            content: "";
        }

        .file-list li.selected > .entity-title > .entity-name,
        .file-list li > .entity-title:hover > .entity-name {
            font-weight: bold;
        }

        .file-list li :is(.expand-icon, .drag-icon):not(.empty) {
            display: block;
            position: absolute;
            top: 10px;
            width: var(--expand-icon-size);
            height: var(--expand-icon-size);
            mask-size: cover;
            mask-repeat: no-repeat;
            background-color: var(--text-color);
            content: "";
        }

        .file-list li .drag-icon:not(.empty) {
            right: 0;
            background-color: transparent;
        }

        .draggable > .entity-title:hover > .drag-icon {
            background-color: var(--text-color);
            mask-image: url("/assets/primeicons/ellipsis-v.svg") !important;
        }

        .file-list li.node-root.closed > .entity-title > .expand-icon {
            mask-image: url("/assets/primeicons/angle-right.svg");
        }

        .file-list li.node-root.open > .entity-title > .expand-icon {
            mask-image: url("/assets/primeicons/angle-down.svg");
        }

        .file-list .domain-root .entity-name:before {
            mask-image: url("/assets/primeicons/cloud.svg");
        }
        .file-list .product-root .entity-name:before {
            mask-image: url("/assets/primeicons/box.svg");
        }
        .file-list .process-root .entity-name:before {
            mask-image: url("/assets/primeicons/share-alt.svg");
        }

        .file-list .node-root .placeholder {
            color: #aaa;
            cursor: unset;
        }

        .context-menu-bg {
            position: absolute;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            z-index: 100;
        }

        .context-menu-bg > menu {
            background: var(--stekz-primary-background);
            color: var(--text-color);
            box-shadow: rgba(0, 0, 0, 0.25) 2px 2px 8px;
            list-style: none;
            margin: 0;
            padding: 0;
        }
        .context-menu-bg > menu > li {
            padding: 8px 16px;
            cursor: pointer;
        }

        .context-menu-bg > menu > li:hover {
            color: var(--text-color-primary-contrast);
            background: var(--stekz-primary-color);
        }
    `;

    @property({ attribute: "data-selected-domain-id" })
    selectedDomainId: string = "";

    @property({ attribute: "data-selected-product-id" })
    selectedProductId: string = "";

    @property({ attribute: "data-selected-process-id" })
    selectedProcessId: string = "";

    @property({ attribute: "data-stek-id" })
    stekId: string = "";

    @state()
    dragInfo: DragInfo | null = null;

    @state()
    domains: DomainInMenu[] = [];

    // The loading icon is only shown while the initial tree is being loaded,
    // after this the tree will be updated async without showing the loading
    // icon, unless you switch the stek.
    @state()
    loading = true;

    @state()
    contextMenu: {
        // Points to the entity that we open a context menu for.
        entity: DomainInMenu | ProductInMenu | ProcessInMenu;
        x: number;
        y: number;
    } | null = null;

    @state()
    creatingDomain = false;

    @state()
    newDomainName: string = "";

    deselectAll() {
        this.domains.forEach((domain) => {
            domain.state.selected = false;
            domain.state.open = false;
            domain.products.forEach((product) => {
                product.state.selected = false;
                product.state.open = false;
                product.processes.forEach((process) => {
                    process.state.selected = false;
                    process.state.open = false;
                });
            });
        });
    }

    // Deselect all entities that do not have the given identity.
    deselectOthers(entityId: string) {
        this.domains.forEach((domain) => {
            if (domain.identity !== entityId) {
                domain.state.selected = false;
            }
            domain.products.forEach((product) => {
                if (product.identity !== entityId) {
                    product.state.selected = false;
                }
                product.processes.forEach((process) => {
                    if (process.identity !== entityId) {
                        process.state.selected = false;
                    }
                });
            });
        });
    }

    willUpdate(changedProperties: PropertyValues<this>) {
        super.willUpdate(changedProperties);

        // Only update the tree when the stekId updates, avoid updating the tree
        // when the component renders for the first time.
        if (changedProperties.has("stekId") && this.stekId) {
            this.placeholderEntities = { [this.stekId]: [] };
        }

        // Whenever the selected domain, product or process changes, update
        // the state in the tree to reflect the new selection.
        if (
            changedProperties.has("selectedDomainId") ||
            changedProperties.has("selectedProductId") ||
            changedProperties.has("selectedProcessId")
        ) {
            this.select(
                this.selectedDomainId,
                this.selectedProductId,
                this.selectedProcessId
            );
            this.requestUpdate();
        }
    }

    // Either returns a default state, or if the entity was already in the
    // tree, returns the state for that entity from the previous tree.
    getStateForEntity(
        entity: Omit<DomainInMenu | ProductInMenu | ProcessInMenu, "state">,
        oldEntity?: DomainInMenu | ProductInMenu | ProcessInMenu
    ) {
        const defaultState = getDefaultMenuState();

        // From a process node, you can never be 'creating' a child node.
        if (isProcessInMenu(entity)) {
            defaultState.creating = false;
        }

        if (!oldEntity) {
            return defaultState;
        }

        // Override the default state with the state from the previous tree,
        // this way, the state of the tree is preserved when the tree is
        // updated.
        return {
            ...defaultState,
            ...oldEntity.state,
        };
    }

    async updateMenu() {
        const domains: DomainInMenu[] = [];
        if (!this.manager.worktree.value) {
            return;
        }
        // Copy the worktree and enrich it with some state information.
        this.manager.worktree.value.domains.forEach((domain: DomainInTree) => {
            const baseDomainInMenu: Omit<DomainInMenu, "state"> = {
                name: domain.name,
                identity: domain.identity,
                products: [],
            };
            const oldDomainInMenu = this.domains.find(
                (d) => d.identity === domain.identity
            );

            const domainInMenu: DomainInMenu = {
                ...baseDomainInMenu,
                state: this.getStateForEntity(
                    baseDomainInMenu,
                    oldDomainInMenu
                ),
            };

            domain.products.forEach((product: ProductInTree) => {
                const baseProductInMenu: Omit<ProductInMenu, "state"> = {
                    domain: domainInMenu,
                    name: product.name,
                    identity: product.identity,
                    processes: [],
                };
                const oldProductInMenu = oldDomainInMenu?.products.find(
                    (p) => p.identity === product.identity
                );

                const productInMenu: ProductInMenu = {
                    ...baseProductInMenu,
                    state: this.getStateForEntity(
                        baseProductInMenu,
                        oldProductInMenu
                    ),
                };

                product.processes.forEach((process: ProcessInTree) => {
                    const baseProcessInMenu: Omit<ProcessInMenu, "state"> = {
                        product: productInMenu,
                        name: process.name,
                        identity: process.identity,
                    };
                    const oldProcessInMenu = oldProductInMenu?.processes.find(
                        (p) => p.identity === process.identity
                    );
                    const processInMenu: ProcessInMenu = {
                        ...baseProcessInMenu,
                        state: this.getStateForEntity(
                            baseProcessInMenu,
                            oldProcessInMenu
                        ),
                    };
                    productInMenu.processes.push(processInMenu);
                });

                domainInMenu.products.push(productInMenu);
            });

            domains.push(domainInMenu);
        });
        this.domains = domains;

        // Immediately update the state of the currently selected entity, this
        // allows the tree to reflect the current selection upon a page load.
        this.select(
            this.selectedDomainId,
            this.selectedProductId,
            this.selectedProcessId
        );
        this.loading = false;
        this.requestUpdate();
    }

    async refreshTree() {
        // Sync the worktree store and update the tree
        await this.store.sync();
        this.updateMenu();
    }

    createProcessTemplate(_domain: DomainInMenu, product: ProductInMenu) {
        if (!product.state.creating) {
            return;
        }

        return html` <li class="node-root process-root new new-input">
            <div class="entity-name">
                <input
                    placeholder="Name your process"
                    type="text"
                    value=${product.state.newProcessName}
                    @blur=${() => {
                        product.state!.creating = false;
                        this.requestUpdate();
                    }}
                    @keydown=${this.onKeydownForCreationEvent(product)}
                    @input=${(e: InputEvent) => {
                        product.state!.newProcessName = (
                            e.target as HTMLInputElement
                        ).value;
                    }}
                />
            </div>
        </li>`;
    }
    onKeydownForCreationEvent(
        entity: DomainInMenu | ProductInMenu | NewDomain
    ) {
        return (e: KeyboardEvent) => {
            // Some special logic in case the user is creating a new domain.
            // (i.e. not a child entity of a domain or product)
            if (!isDomainInMenu(entity) && !isProductInMenu(entity)) {
                if (e.key === "Escape") {
                    this.creatingDomain = false;
                    this.newDomainName = "";
                    this.requestUpdate();
                } else if (e.key === "Enter") {
                    this.creatingDomain = false;
                    this.createEntity({
                        name: this.newDomainName,
                    });
                    this.newDomainName = "";
                }
                return;
            }

            if (e.key === "Escape") {
                entity.state.creating = false;

                if (isDomainInMenu(entity)) {
                    this.newDomainName = "";
                } else if (isProductInMenu(entity)) {
                    entity.state.newProcessName = "";
                }

                this.requestUpdate();
            } else if (e.key === "Enter") {
                entity.state.creating = false;
                this.requestUpdate();
                // Be sure to copy the object, as the reference
                // to the object might be lost if the tree gets
                // updated in the meantime
                this.createEntity({
                    ...entity,
                    state: { ...entity.state },
                });

                if (isDomainInMenu(entity)) {
                    entity.state.newProductName = "";
                } else if (isProductInMenu(entity)) {
                    entity.state.newProcessName = "";
                }
            }
        };
    }

    createProductTemplate(domain: DomainInMenu) {
        if (!domain.state.creating) {
            return;
        }

        return html` <li class="node-root product-root closed new new-input">
            <div class="entity-name">
                <input
                    placeholder="Name your product / service"
                    type="text"
                    value=${domain.state.newProductName}
                    @blur=${() => {
                        domain.state.creating = false;
                        this.requestUpdate();
                    }}
                    @keydown=${this.onKeydownForCreationEvent(domain)}
                    @input=${(e: InputEvent) => {
                        domain.state.newProductName = (
                            e.target as HTMLInputElement
                        ).value;
                    }}
                />
            </div>
        </li>`;
    }

    @state()
    placeholderEntities: {
        [parentNanoid: string]: (
            | DomainInMenu
            | ProductInMenu
            | ProcessInMenu
        )[];
    } = {};

    // Returns a function that can be called to remove a specific placeholder
    // entity from the tree.
    removePlaceholderEntityFn(
        parentEntity: DomainInMenu | ProductInMenu | ProcessInMenu | NewDomain,
        name: string
    ) {
        // When this function is called, we need to check whether it was called
        // or the creation of the entity we're removing the placeholder for,
        // as it could also be called for the creation of different entities.
        return (newTree: Worktree) => {
            // Flatten the entire tree of entities and get an object of parent nanoids
            // and the names of all children for each of those nanoids.
            const allEntities: Record<string, string[]> = {};
            allEntities[this.stekId] ??= [];
            newTree.domains.forEach((domain) => {
                allEntities[domain.identity] ??= [];
                allEntities[this.stekId].push(domain.name);
                domain.products.forEach((product) => {
                    allEntities[domain.identity].push(product.name);
                    allEntities[product.identity] ??= [];
                    product.processes.forEach((process) => {
                        allEntities[product.identity].push(process.name);
                    });
                });
            });

            // The 'NewDomain' entity is somewhat of a special case, as it has no parent entity, for this
            // we can use the stekId as the parent identity.
            const parentIdentity =
                "identity" in parentEntity
                    ? parentEntity.identity
                    : this.stekId;
            if (!(parentIdentity in allEntities)) {
                return;
            }

            // Only remove the name when it exists in the new tree.
            if (!allEntities[parentIdentity].includes(name)) {
                return;
            }

            this.removePlaceholderEntity(parentIdentity, name);
        };
    }

    removePlaceholderEntity(parentIdentity: string, name: string) {
        const placeHolderIndex = this.placeholderEntities[
            parentIdentity
        ].findIndex((entity) => entity.name === name);
        if (placeHolderIndex === -1) {
            return;
        }

        this.placeholderEntities[parentIdentity].splice(placeHolderIndex, 1);

        this.requestUpdate();
    }

    // Add a temporary entity with the placeholder state true
    // to allow the UI to show a placeholder during creation.
    createPlaceholderEntity(
        entity: DomainInMenu | ProductInMenu | ProcessInMenu | NewDomain
    ) {
        if (isDomainInMenu(entity)) {
            const placeholderEntity = {
                identity: "",
                name: entity.state.newProductName,
                state: {
                    ...getDefaultMenuState(),
                    placeholder: true,
                },
                domain: entity,
                processes: [],
            } as ProductInMenu;
            this.placeholderEntities[entity.identity] ??= [];
            this.placeholderEntities[entity.identity].push(placeholderEntity);

            this.requestUpdate();
            // Return a callback that can be called to remove the placeholder
            // if it still exists.
            return this.removePlaceholderEntityFn(
                entity,
                entity.state.newProductName || ""
            );
        } else if (isProductInMenu(entity)) {
            const placeholderEntity = {
                identity: "",
                name: entity.state.newProcessName,
                product: entity,
                state: {
                    ...getDefaultMenuState(),
                    placeholder: true,
                },
                userArtefactGroupId: null,
            } as ProcessInMenu;
            this.placeholderEntities[entity.identity] ??= [];
            this.placeholderEntities[entity.identity].push(placeholderEntity);

            this.requestUpdate();
            // Return a callback that can be called to remove the placeholder
            // if it still exists.
            return this.removePlaceholderEntityFn(
                entity,
                entity.state.newProcessName || ""
            );
        } else if (!isProcessInMenu(entity)) {
            const placeholderEntity: DomainInMenu = {
                identity: "",
                name: this.newDomainName,
                state: {
                    ...getDefaultMenuState(),
                    placeholder: true,
                },
                products: [],
            };
            this.placeholderEntities[this.stekId] ??= [];
            this.placeholderEntities[this.stekId].push(placeholderEntity);
            this.requestUpdate();

            // Return a callback that can be called to remove the placeholder
            // if it still exists.
            return this.removePlaceholderEntityFn(entity, this.newDomainName);
        }
    }

    async createEntity(entity: NewDomain | ProductInMenu | ProcessInMenu) {
        const removePlaceholder = this.createPlaceholderEntity({ ...entity });
        let promise: Promise<Domain | Product | Process | undefined>;
        if (isDomainInMenu(entity)) {
            if (!entity.state?.newProductName) {
                return;
            }
            promise = useProductsStore().create(
                entity.state.newProductName,
                entity.identity
            );
        } else if (isProductInMenu(entity)) {
            if (!entity.state?.newProcessName) {
                return;
            }
            promise = useProcessesStore().create(
                entity.state.newProcessName,
                entity.identity
            );
        } else {
            // When the entity is none of the above, we're creating a new root-level
            // domain.
            promise = useDomainsStore().create(entity.name);
            // Clear the domain name, otherwise if someone starts creating a
            // new domain again, the old name will still be there.
            this.newDomainName = "";
        }

        const parentIdentity =
            "identity" in entity ? entity.identity : this.stekId;

        const unwatch = watch(this.manager.worktree, (value) => {
            if (removePlaceholder && value) {
                removePlaceholder(value);
            }
        });

        try {
            const result = await promise;
            if (result) {
                await this.store.sync();
                if (isDomainInMenu(entity)) {
                    const { product } = this.find({
                        productId: result.identity,
                    });
                    if (product) {
                        this.navigate(product);
                    }
                } else if (isProductInMenu(entity)) {
                    const { process } = this.find({
                        processId: result.identity,
                    });
                    if (process) {
                        this.navigate(process);
                    }
                } else {
                    const { domain } = this.find({ domainId: result.identity });
                    if (domain) {
                        this.navigate(domain);
                    }
                }
            }
        } finally {
            // Always remove the placeholder entity.
            this.removePlaceholderEntity(parentIdentity, entity.name);
            unwatch();
        }
    }

    // Always show the 'create domain' entry.
    createDomainTemplate() {
        if (this.creatingDomain) {
            return html`<li class="node-root domain-root new new-input">
                <div class="entity-name">
                    <input
                        placeholder="Name your domain"
                        type="text"
                        value=${this.newDomainName}
                        @blur=${() => {
                            this.creatingDomain = false;
                            this.newDomainName = "";
                            this.requestUpdate();
                        }}
                        @keydown=${this.onKeydownForCreationEvent({
                            name: this.newDomainName,
                        })}
                        @input=${(e: InputEvent) => {
                            this.newDomainName = (
                                e.target as HTMLInputElement
                            ).value;
                        }}
                    />
                </div>
            </li>`;
        }

        return html`<li class="node-root domain-root new">
            <div
                class="entity-name new-entity"
                @click=${async (e: MouseEvent) => {
                    e.stopPropagation();
                    this.creatingDomain = true;

                    // Wait until Lit is done updating the DOM before trying
                    // to access the input field.
                    this.requestUpdate();

                    await this.updateComplete;

                    const inputField = this.renderRoot.querySelector("input");
                    // Focus the input field.
                    inputField?.focus();
                }}
            >
                New domain
            </div>
        </li>`;
    }

    domainNodeTemplate(domain: DomainInMenu) {
        // Note, forcing a conversion with 'as' to avoid expensive type checks.
        const products =
            domain.identity in this.placeholderEntities
                ? ([
                      ...domain.products,
                      ...this.placeholderEntities[domain.identity],
                  ] as ProductInMenu[])
                : domain.products;
        products.sort((a, b) => a.name.localeCompare(b.name));

        const liClasses = {
            "node-root": true,
            "domain-root": true,
            selected: domain.state.selected,
            open: domain.state.open,
            closed: !domain.state.open,
            "drop-target":
                domain.identity === this.dragInfo?.dropTarget?.identity,
        };

        const divClasses = {
            "entity-name": true,
            placeholder: domain.state.placeholder || false,
        };

        return html`<li
            @dragover=${this.onDragOver}
            @dragenter=${_.bind(this.onDragEnter, this, _, domain)}
            @dragleave=${_.bind(this.onDragLeave, this, _, domain)}
            @drop=${_.bind(this.onDrop, this, _, domain)}
            class=${classMap(liClasses)}
            @click=${this.onClickToOpenEntityEvent(domain)}
            @contextmenu=${this.onContextMenuEvent(domain)}
        >
            <div class="entity-title">
                <span
                    class="expand-icon ${products.length == 0 ? "empty" : ""}"
                ></span>
                <a
                    class=${classMap(divClasses)}
                    title=${domain.name}
                    @click=${this.onEntityNameClickEvent(domain)}
                    href=${this.getUrl(domain)}
                >
                    ${this.entityNameTemplate(domain)}
                </a>
            </div>
            <ul>
                ${products.map((product) =>
                    this.productNodeTemplate(domain, product)
                )}
                ${this.isEditing.value
                    ? this.createProductTemplate(domain)
                    : nothing}
            </ul>
        </li>`;
    }

    onEntityNameClickEvent(
        entity: DomainInMenu | ProductInMenu | ProcessInMenu
    ) {
        return (e: MouseEvent) => {
            e.stopPropagation();
            // When the user holds ctrl while clicking the link, don't prevent
            // the default behavior, as this will open the link in a new tab.
            if (e.ctrlKey) {
                return;
            } else {
                e.preventDefault();
            }

            // Do nothing when showing a placeholder.
            if (entity.state.placeholder) {
                return;
            }

            // Do nothing when the entity is being renamed.
            if (entity.state.renaming) {
                return;
            }

            // When the entity is already selected, open the menu, except
            // when the entity is a process.
            if (entity.state.selected && !isProcessInMenu(entity)) {
                this.openOrCloseEntity(entity, true);
            }

            this.navigate(entity);
            this.requestUpdate();
        };
    }

    onClickToOpenEntityEvent(entity: DomainInMenu | ProductInMenu) {
        // When showing a placeholder, don't allow any click events.
        if (entity.state.placeholder) {
            return null;
        }

        return (e: MouseEvent) => {
            e.stopPropagation();

            // Do nothing when in the process of creating a child entity.
            if (entity.state.creating) {
                return;
            }

            this.openOrCloseEntity(entity, !entity.state.open);
            this.requestUpdate();
        };
    }

    productNodeTemplate(domain: DomainInMenu, product: ProductInMenu) {
        // Note, forcing a conversion with 'as' to avoid expensive type checks.
        const processes =
            product.identity in this.placeholderEntities
                ? ([
                      ...product.processes,
                      ...this.placeholderEntities[product.identity],
                  ] as ProcessInMenu[])
                : product.processes;
        processes.sort((a, b) => a.name.localeCompare(b.name));

        const liClasses = {
            "node-root": true,
            "product-root": true,
            selected: product.state?.selected,
            open: product.state.open,
            closed: !product.state.open,
            renaming: product.state.renaming,
            draggable: this.isEditing.value,
            "drop-target":
                product.identity === this.dragInfo?.dropTarget?.identity,
        };

        const divClasses = {
            "entity-name": true,
            placeholder: product.state.placeholder || false,
        };

        return html`<li
            draggable=${this.isEditing.value ? "true" : "false"}
            @dragstart=${_.bind(this.onDragStart, this, _, product)}
            @dragend=${_.bind(this.onDragEnd, this, _, product)}
            @dragover=${this.onDragOver}
            @dragenter=${_.bind(this.onDragEnter, this, _, product)}
            @dragleave=${_.bind(this.onDragLeave, this, _, product)}
            @drop=${_.bind(this.onDrop, this, _, product)}
            class=${classMap(liClasses)}
            @click=${this.onClickToOpenEntityEvent(product)}
            @contextmenu=${this.onContextMenuEvent(product)}
        >
            <div class="entity-title" draggable="false">
                <span
                    draggable="false"
                    class="expand-icon ${processes.length == 0 ? "empty" : ""}"
                ></span>
                <a
                    draggable="false"
                    class=${classMap(divClasses)}
                    title=${product.name}
                    @click=${this.onEntityNameClickEvent(product)}
                    href=${this.getUrl(product)}
                >
                    ${this.entityNameTemplate(product)}
                </a>
                <span draggable="false" class="drag-icon"></span>
            </div>
            <ul draggable="false">
                ${processes.map((process) =>
                    this.processNodeTemplate(domain, product, process)
                )}
                ${this.isEditing.value
                    ? this.createProcessTemplate(domain, product)
                    : nothing}
            </ul>
        </li>`;
    }

    entityNameTemplate(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        if (!entity.state.renaming) {
            return html`${entity.name}`;
        }

        return html`<input
            placeholder=${entity.state.newName}
            type="text"
            value=${entity.state.newName}
            @blur=${() => {
                entity.state.renaming = false;
                entity.state.newName = "";
                this.requestUpdate();
            }}
            @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Escape") {
                    entity.state.renaming = false;
                    entity.state.newName = "";
                    this.requestUpdate();
                } else if (e.key === "Enter") {
                    this.renameEntity(entity);
                }
            }}
            @input=${(e: InputEvent) => {
                entity.state.newName = (e.target as HTMLInputElement).value;
            }}
        />`;
    }

    getDeleteEventDetail(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        const detail = {
            name: entity.name,
            identity: entity.identity,
        } as {
            name: string;
            identity: string;
            entityType: "domain" | "product" | "process";
            domainName: string;
            productName?: string;
            processName?: string;
        };
        if (isDomainInMenu(entity)) {
            detail["entityType"] = "domain";
            detail["domainName"] = entity.name;
        } else if (isProductInMenu(entity)) {
            detail["entityType"] = "product";
            detail["domainName"] = entity.domain.name;
            detail["productName"] = entity.name;
        } else if (isProcessInMenu(entity)) {
            detail["entityType"] = "process";
            detail["domainName"] = entity.product.domain.name;
            detail["productName"] = entity.product.name;
            detail["processName"] = entity.name;
        }
        return detail;
    }

    getUrl(
        entity:
            | (NewDomain & { identity: string })
            | DomainInMenu
            | ProductInMenu
            | ProcessInMenu
    ) {
        if (!entity.identity) {
            return undefined;
        }
        const { organization } = useOrganizationStore();
        const { stek } = useStekManager();
        if (isProcessInMenu(entity)) {
            return router.resolve({
                name: "processPage",
                params: {
                    orgName: organization?.name,
                    stekName: stek.value?.name,
                    processId: entity.identity,
                },
            }).fullPath;
        } else if (isProductInMenu(entity)) {
            return router.resolve({
                name: "productPage",
                params: {
                    orgName: organization?.name,
                    stekName: stek.value?.name,
                    productId: entity.identity,
                },
            }).fullPath;
        } else {
            // Assume domain
            return router.resolve({
                name: "domainPage",
                params: {
                    orgName: organization?.name,
                    stekName: stek.value?.name,
                    domainId: entity.identity,
                },
            }).fullPath;
        }
    }

    navigate(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        const url = this.getUrl(entity);
        this.selectEntity(entity);
        if (url) {
            router.push(url);
        }
    }

    onContextMenuEvent(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        return (e: MouseEvent) => {
            // See https://gitlab.com/stekz/mystekz/mystekz/-/issues/130#note_2099795679
            document.getElementById("sidebar")?.classList.add("on-top");
            e.preventDefault();
            e.stopPropagation();
            const rect = this.getBoundingClientRect();
            this.contextMenu = {
                entity,
                // Position relative to the component
                x: e.clientX - rect.x,
                y: e.clientY - rect.y,
            };
            this.requestUpdate();
        };
    }

    async renameEntity(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        if (!entity || !entity.state) {
            return;
        }

        if (entity.name === entity.state.newName || !entity.state.newName) {
            entity.state.renaming = false;
            this.requestUpdate();
            return;
        }

        this.dispatchEvent(
            new CustomEvent("renaming", {
                composed: true,
                bubbles: true,
                detail: true,
            })
        );

        if (isDomainInMenu(entity)) {
            const { domain } = useDomainManager();
            if (!domain.value) {
                return;
            }
            domain.value.name = entity.state.newName;
        } else if (isProductInMenu(entity)) {
            const { product } = useProductManager();
            if (!product.value) {
                return;
            }
            product.value.name = entity.state.newName;
        } else if (isProcessInMenu(entity)) {
            const { process } = useProcessManager();
            if (!process.value) {
                return;
            }
            process.value.name = entity.state.newName;
        } else {
            throw new Error("Unknown entity type");
        }

        // Assume the new name;
        entity.state.renaming = false;
        entity.name = entity.state.newName;
        this.requestUpdate();
    }

    processNodeTemplate(
        _domain: DomainInMenu,
        _product: ProductInMenu,
        process: ProcessInMenu
    ) {
        const liClasses = {
            "node-root": true,
            "process-root": true,
            selected: process.state.selected,
            open: process.state.open,
            closed: !process.state.open,
            draggable: this.isEditing.value,
        };

        const divClasses = {
            "entity-name": true,
            placeholder: process.state.placeholder || false,
            renaming: process.state.renaming,
        };

        return html`<li
            draggable=${this.isEditing.value ? "true" : "false"}
            @dragstart=${_.bind(this.onDragStart, this, _, process)}
            @dragend=${_.bind(this.onDragEnd, this, _, process)}
            @mouseover=${(e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            class=${classMap(liClasses)}
            @contextmenu=${this.onContextMenuEvent(process)}
        >
            <div class="entity-title" draggable="false">
                <a
                    draggable="false"
                    class=${classMap(divClasses)}
                    title=${process.name}
                    @click=${this.onEntityNameClickEvent(process)}
                    href=${this.getUrl(process)}
                >
                    ${this.entityNameTemplate(process)}
                </a>
                <span draggable="false" class="drag-icon"></span>
            </div>
        </li>`;
    }

    openOrCloseEntity(
        entity: DomainInMenu | ProductInMenu | ProcessInMenu,
        open: boolean
    ) {
        let hasChildren = false;
        let isCreating = false;
        if (isDomainInMenu(entity)) {
            hasChildren = entity.products.length > 0;
            isCreating = entity.state.creating || false;
        } else if (isProductInMenu(entity)) {
            hasChildren = entity.processes.length > 0;
            isCreating = entity.state.creating || false;
        }

        // Always close or keep the entity closed when it has no children, unless we're creating a new child.
        if (!hasChildren && !isCreating) {
            entity.state.open = false;
            return;
        }

        entity.state.open = open;
    }

    selectEntity(entity: DomainInMenu | ProductInMenu | ProcessInMenu) {
        this.deselectOthers(entity.identity);
        this.openOrCloseEntity(entity, true);
        entity.state.selected = true;
        this.requestUpdate();
    }

    find({
        domainId,
        productId,
        processId,
    }: {
        domainId?: string;
        productId?: string;
        processId?: string;
    }) {
        let domain: DomainInMenu | undefined = undefined;
        let product: ProductInMenu | undefined = undefined;
        let process: ProcessInMenu | undefined = undefined;

        // Note, we're not using the search functions from the worktree
        // manager as those will search in a different tree than the one we're
        // using in this component.
        if (processId) {
            for (const d of this.domains) {
                for (const p of d.products) {
                    for (const pr of p.processes) {
                        if (pr.identity === processId) {
                            domain = d;
                            product = p;
                            process = pr;
                            break;
                        }
                    }
                }
            }
        } else if (productId) {
            for (const d of this.domains) {
                for (const p of d.products) {
                    if (p.identity === productId) {
                        domain = d;
                        product = p;
                        break;
                    }
                }
            }
        } else if (domainId) {
            domain = this.domains.find(
                (domain: DomainInTree) => domain.identity === domainId
            );
        }
        return { domain, product, process };
    }

    // Find the most-nested node which matches the given domain, product and
    // process name and select it. Also open any nodes for parents of the
    // selected node along the way.
    async select(domainId: string, productId: string, processId: string) {
        if (!domainId && !productId && !processId) {
            this.deselectAll();
            return;
        }

        const { domain, product, process } = this.find({
            domainId,
            productId,
            processId,
        });

        if (process && product && domain) {
            // If a nested process is selected, also open the parents.
            domain.state.open = true;
            product.state.open = true;
            this.selectEntity(process);
        } else if (product && domain) {
            // If a nested product is selected, also open the parent domain.
            domain.state.open = true;
            this.selectEntity(product);
        } else if (domain) {
            if (!domain) {
                this.deselectAll();
                return;
            }
            this.selectEntity(domain);
            return;
        }
    }

    // Depending on the type of entity, we show different entity creation options.
    contextMenuCreateNodeTemplate() {
        if (!this.contextMenu) {
            return;
        }
        const entity = this.contextMenu.entity;
        if (isDomainInMenu(entity)) {
            return html`<li
                @click=${async () => {
                    entity.state.creating = true;
                    this.openOrCloseEntity(entity, true);
                    document
                        .getElementById("sidebar")
                        ?.classList.remove("on-top");
                    this.contextMenu = null;
                    this.requestUpdate();

                    // Wait until Lit is done updating the DOM before trying
                    // to access the input field.
                    await this.updateComplete;

                    const inputField = this.renderRoot.querySelector("input");
                    // Focus the input field.
                    inputField?.focus();
                }}
            >
                New product / service
            </li>`;
        }

        if (isProductInMenu(this.contextMenu.entity)) {
            return html`<li
                @click=${async () => {
                    entity.state.creating = true;
                    this.openOrCloseEntity(entity, true);
                    document
                        .getElementById("sidebar")
                        ?.classList.remove("on-top");
                    this.contextMenu = null;
                    this.requestUpdate();

                    // Wait until Lit is done updating the DOM before trying
                    // to access the input field.
                    await this.updateComplete;

                    const inputField = this.renderRoot.querySelector("input");
                    // Focus the input field.
                    inputField?.focus();
                }}
            >
                New process
            </li>`;
        }

        return nothing;
    }

    contextMenuRenameNodeTemplate() {
        if (!this.contextMenu) {
            return nothing;
        }
        return html` <li
            @click=${async () => {
                // Navigate to the item, since we can currently only update
                // the currently active entity.
                this.navigate(this.contextMenu!.entity);
                // Start with the name of the entity as the new name.
                this.contextMenu!.entity.state.newName =
                    this.contextMenu!.entity.name;
                this.contextMenu!.entity.state.renaming = true;
                document.getElementById("sidebar")?.classList.remove("on-top");
                this.contextMenu = null;

                // Wait until Lit is done updating the DOM before trying
                // to access the input field.
                await this.updateComplete;

                const inputField = this.renderRoot.querySelector("input");
                if (inputField) {
                    // Focus the input field.
                    inputField.focus();
                    // A trick to make the cursor move to the end of the input field
                    inputField.selectionStart = 1000;
                    inputField.selectionEnd = 1000;
                }
            }}
        >
            Rename
        </li>`;
    }

    contextMenuTemplate() {
        if (this.contextMenu === null || !this.isEditing.value) {
            return;
        }

        // Clicking anywhere on the background removes the context menu
        // background again.
        return html`<div
            class="context-menu-bg"
            @click=${() => {
                // See: https://gitlab.com/stekz/mystekz/mystekz/-/issues/130#note_2099795679
                document.getElementById("sidebar")?.classList.remove("on-top");
                this.contextMenu = null;
            }}
            @contextmenu=${(e: MouseEvent) => {
                // When right-clicking again while a context menu is open,
                // just close the context menu.
                e.preventDefault();
                document.getElementById("sidebar")?.classList.remove("on-top");
                this.contextMenu = null;
            }}
        >
            <menu
                style="
                  position: absolute;
                  top: ${this.contextMenu.y}px;
                  left: ${this.contextMenu.x}px;
                "
            >
                ${this.contextMenuCreateNodeTemplate()}
                ${this.contextMenuRenameNodeTemplate()}
                <li
                    @click=${() => {
                        this.dispatchEvent(
                            new CustomEvent("delete", {
                                composed: true,
                                bubbles: true,
                                detail: this.getDeleteEventDetail(
                                    this.contextMenu!.entity
                                ),
                            })
                        );
                    }}
                >
                    Delete
                </li>
            </menu>
        </div>`;
    }

    render() {
        if (this.loading) {
            return html`<ul class="file-list root">
                <li class="node-root domain-root closed">
                    <div class="entity-name">Loading..</div>
                </li>
            </ul>`;
        }

        // Note, forcing a conversion with 'as' to avoid expensive type checks.
        const domains =
            this.stekId in this.placeholderEntities
                ? ([
                      ...this.placeholderEntities[this.stekId],
                      ...this.domains,
                  ] as DomainInMenu[])
                : this.domains;
        domains.sort((a, b) => a.name.localeCompare(b.name));

        return html`<div>
            ${this.contextMenuTemplate()}
            <ul class="file-list root">
                ${domains.map((domain) => this.domainNodeTemplate(domain))}
                ${this.isEditing.value ? this.createDomainTemplate() : nothing}
            </ul>
        </div>`;
    }

    async onDragStart(
        dragEvent: DragEvent,
        entity: ProcessInMenu | ProductInMenu
    ) {
        dragEvent.stopPropagation();
        dragEvent.dataTransfer!.effectAllowed = "move";

        this.dragInfo = { entity: entity, dropSuccess: false };

        const target = dragEvent.target as HTMLElement;

        target.classList.add("dragging");
    }

    async onDragEnd(dragEvent: DragEvent, _: ProcessInMenu | ProductInMenu) {
        const target = dragEvent.target as HTMLElement;

        // Wait for 1s for the drop effect to be processed.
        // If it was not dropped in a valid target, we will remove the dragging class.
        setTimeout(() => {
            if (!this.dragInfo?.dropSuccess) {
                // If the drop effect is not 'move', it means the item was not dropped
                // in a valid drop target, so we need to restore the element.
                target.classList.remove("dragging");
            }
            this.updateMenu();
        }, 500);
    }
    async onDragOver(dragEvent: DragEvent) {
        dragEvent.preventDefault();
    }

    async onDragEnter(
        dragEvent: DragEvent,
        target: DomainInMenu | ProductInMenu
    ) {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();

        if (!this.dragInfo || !this.dragInfo.entity) {
            return;
        }

        let isDropTarget = false;
        let isParentOfDropTarget = false;

        if (isDomainInMenu(target)) {
            if (isProductInMenu(this.dragInfo.entity)) {
                isDropTarget = true;
            } else if (isProcessInMenu(this.dragInfo.entity)) {
                isParentOfDropTarget = true;
            }
        } else if (isProductInMenu(target)) {
            if (isProcessInMenu(this.dragInfo.entity)) {
                isDropTarget = true;
            }
        }

        if (isParentOfDropTarget) {
            const expandTimeoutId = setTimeout(() => {
                // Open the entity after a short delay
                this.openOrCloseEntity(target, true);
                this.requestUpdate();
            }, 1000);

            target.state.expandTimeoutId = expandTimeoutId;
        } else if (isDropTarget) {
            this.dragInfo.dropTarget = target;
            this.requestUpdate();
        }
    }

    async onDragLeave(
        dragEvent: DragEvent,
        target: DomainInMenu | ProductInMenu
    ) {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();

        if (target.state.expandTimeoutId !== undefined) {
            clearTimeout(target.state.expandTimeoutId);
            target.state.expandTimeoutId = undefined;
        }

        if (this.dragInfo?.dropTarget === target) {
            this.dragInfo.dropTarget = undefined;
        }
    }

    async onDrop(dragEvent: DragEvent, target: DomainInMenu | ProductInMenu) {
        dragEvent.preventDefault();

        if (!this.dragInfo || !this.dragInfo.entity) {
            return;
        }
        const entity = this.dragInfo.entity;

        if (isProductInMenu(target) && isProcessInMenu(entity)) {
            if (target.identity === entity.product?.identity) {
                // Don't allow moving a process to the same product.
                console.warn("Process is already in the target product.");
            } else {
                this.dispatchEvent(
                    new CustomEvent("move", {
                        composed: true,
                        bubbles: true,
                        detail: {
                            entityType: EntityType.PROCESS,
                            entity: entity,
                            target: target,
                        },
                    })
                );
                this.dragInfo.dropSuccess = true;
            }
        } else if (isDomainInMenu(target) && isProductInMenu(entity)) {
            if (target.identity === entity.domain?.identity) {
                // Don't allow moving a product to the same domain.
                console.warn("Product is already in the target domain.");
            } else {
                this.dispatchEvent(
                    new CustomEvent("move", {
                        composed: true,
                        bubbles: true,
                        detail: {
                            entityType: EntityType.PRODUCT,
                            entity: entity,
                            target: target,
                        },
                    })
                );
                this.dragInfo.dropSuccess = true;
            }
        } else {
            // The drop was incorrect, so we don't allow the drop.
        }

        this.renderRoot
            .querySelectorAll("li.node-root.dragging")
            .forEach((element) => element.classList.remove("dragging"));

        if (this.dragInfo.dropSuccess) {
            this.dragInfo = null;
            await useWorktreeStore().sync();
            this.updateMenu();
        }
    }
}
